// TestHistoryExportDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  RadioGroup,
  Radio,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { exportToConfluence, getConfluenceUrl } from '../../services/testHistoryApiService';
import { useDispatch } from 'react-redux';
import { addJob, updateJob, openJobsDialog } from '../../redux/confluence/confluenceJobsSlice';

export default function TestHistoryExportDialog({
  open,
  onClose,
  onExport,
  totalResults = 0,
  filteredResults = 0,
  hasActiveFilters = false,
  initialRegressionTag = '',
  searchFilters = {},
}) {
  const [exportMode, setExportMode] = useState('filtered');
  const [includeOutbound, setIncludeOutbound] = useState(true);
  const [regressionTag, setRegressionTag] = useState('');
  const [exportFormat, setExportFormat] = useState('excel');
  const [confluenceExporting, setConfluenceExporting] = useState(false);
  const [confluenceResult, setConfluenceResult] = useState(null);
  const [confluenceError, setConfluenceError] = useState(null);
  const [confluenceJobId, setConfluenceJobId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [exportInitiated, setExportInitiated] = useState(false);
  const dispatch = useDispatch();

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setExportMode(hasActiveFilters ? 'filtered' : 'all');
      setIncludeOutbound(true);
      setRegressionTag(initialRegressionTag);
      setExportFormat('excel');
      setConfluenceExporting(false);
      setConfluenceResult(null);
      setConfluenceError(null);
      setConfluenceJobId(null);
      setPolling(false);
      setExportInitiated(false);
    }
  }, [open, hasActiveFilters, initialRegressionTag]);

  const handleExport = async () => {
    if (exportFormat === 'confluence') {
      await handleConfluenceExport();
    } else {
      onExport({
        exportMode,
        includeOutbound,
        regressionTag,
      });
      onClose();
    }
  };

  const handleConfluenceExport = async () => {
    if (!searchFilters.regression_test_id) {
      setConfluenceError('Regression Test ID is required for Confluence export');
      return;
    }

    setConfluenceExporting(true);
    setExportInitiated(true);
    setConfluenceError(null);
    setConfluenceResult(null);

    try {
      const result = await exportToConfluence({
        regression_test_id: searchFilters.regression_test_id,
        device_id: searchFilters.device_id,
        market_id: searchFilters.market_id,
        tester_email: searchFilters.tester_email,
      });
      const jobId = result?.data?.jobId;
      if (!jobId) throw new Error('Job ID missing');

      dispatch(
        addJob({
          jobId,
          regressionTestId: searchFilters.regression_test_id,
          status: 'IN_PROGRESS',
        }),
      );

      setConfluenceJobId(jobId);
      setPolling(true);
    } catch (err) {
      setConfluenceError(err.message);
      setConfluenceExporting(false);
    }
  };

  useEffect(() => {
    if (!polling || !confluenceJobId) return;

    const interval = setInterval(async () => {
      try {
        const res = await getConfluenceUrl(confluenceJobId);
        const job = res?.data?.[0];
        if (!job) return;

        if (job.status === 'COMPLETED') {
          dispatch(
            updateJob({
              jobId: job.job_id,
              updates: {
                status: 'COMPLETED',
                confluenceUrl: job.confluence_url,
              },
            }),
          );

          setConfluenceResult({
            message: 'Export completed',
            link: job.confluence_url,
          });

          setPolling(false);
          setConfluenceExporting(false);
          clearInterval(interval);
        }

        if (job.status === 'FAILED') {
          dispatch(
            updateJob({
              jobId: job.job_id,
              updates: {
                status: 'FAILED',
                error: job.error_details,
              },
            }),
          );

          setConfluenceError(job.error_details || 'Export failed');
          setPolling(false);
          setConfluenceExporting(false);
          clearInterval(interval);
        }
      } catch {
        setPolling(false);
        setConfluenceExporting(false);
        clearInterval(interval);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [polling, confluenceJobId]);

  const handleCancel = () => {
    setExportMode('filtered');
    setIncludeOutbound(true);
    setRegressionTag('');
    setExportFormat('excel');
    setConfluenceExporting(false);
    setConfluenceResult(null);
    setConfluenceError(null);
    onClose();
  };

  const resultsToExport = exportMode === 'all' ? totalResults : filteredResults;
  const showWarning = exportFormat === 'excel' && resultsToExport > 200;
  const isConfluenceDisabled = !searchFilters.regression_test_id;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DownloadIcon />
          Export Test History
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Regression Tag - Only show for Excel */}
        {exportFormat === 'excel' && (
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Regression Tag
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g., 25.103CORE"
              value={regressionTag}
              onChange={(e) => setRegressionTag(e.target.value)}
              helperText="Optional - will be included in filename"
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Export Format Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Export Format
          </Typography>
          <RadioGroup
            value={exportFormat}
            onChange={(e) => {
              setExportFormat(e.target.value);
              setConfluenceResult(null);
              setConfluenceError(null);
            }}
          >
            <FormControlLabel
              value="excel"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChartIcon fontSize="small" sx={{ color: 'success.dark' }} />
                  <Box>
                    <Typography>Excel (.xlsx)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      One sheet per test result with full event and attribute details
                    </Typography>
                  </Box>
                </Box>
              }
            />
            <FormControlLabel
              value="confluence"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon fontSize="small" sx={{ color: 'info.dark' }} />
                  <Box>
                    <Typography>Confluence</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Export to Confluence wiki page (uses current search filters)
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Excel-specific options */}
        {exportFormat === 'excel' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Export Scope
              </Typography>
              <RadioGroup value={exportMode} onChange={(e) => setExportMode(e.target.value)}>
                <FormControlLabel
                  value="filtered"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography>
                        Export Filtered Results ({filteredResults}{' '}
                        {filteredResults === 1 ? 'result' : 'results'})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Export only the results matching current filters
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography>
                        Export All Results ({totalResults}{' '}
                        {totalResults === 1 ? 'result' : 'results'})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Export all results regardless of filters
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            {showWarning && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Large Export - Estimated Time: {Math.round((resultsToExport * 0.5) / 60)}-
                  {Math.round(resultsToExport / 60)} minutes
                </Typography>
                <Typography variant="body2">
                  Exporting {resultsToExport} results requires:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>
                    Fetching full details for each result (~
                    {Math.round((resultsToExport * 0.5) / 60)}-{Math.round(resultsToExport / 60)}{' '}
                    min)
                  </li>
                  <li>Generating Excel file (30-60 sec)</li>
                  <li>
                    File size: {Math.round(resultsToExport * 0.5)}-
                    {Math.round(resultsToExport * 1.5)} MB
                  </li>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Progress will be shown on the Export button. Please keep this tab open during
                  export.
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Include in Export
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeOutbound}
                    onChange={(e) => setIncludeOutbound(e.target.checked)}
                    sx={{
                      '&.Mui-checked': {
                        color: 'secondary.main',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography>Outbound Events</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Kochava, Epsilon, Mixpanel, etc.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </>
        )}

        {/* Confluence-specific options */}
        {exportFormat === 'confluence' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Export Details
            </Typography>

            {isConfluenceDisabled ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Regression Test ID is required for Confluence export. Please set a Regression Test
                ID filter in your search.
              </Alert>
            ) : (
              <Box sx={{ p: 2, bgcolor: 'custom.surfaceAlt', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Regression Test ID:</strong> {searchFilters.regression_test_id}
                </Typography>
                {searchFilters.market_id && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Market:</strong> {searchFilters.market_name || searchFilters.market_id}
                  </Typography>
                )}
                {searchFilters.device_id && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Device:</strong> {searchFilters.device_name || searchFilters.device_id}
                  </Typography>
                )}
                {searchFilters.tester_email && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Tester Email:</strong> {searchFilters.tester_email}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  All results matching these filters will be exported to Confluence.
                </Typography>
              </Box>
            )}

            {exportFormat === 'confluence' &&
              exportInitiated &&
              !confluenceResult &&
              !confluenceError && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Export is running in background.&nbsp;
                    <Link component="button" onClick={() => dispatch(openJobsDialog())}>
                      Click here to view export status
                    </Link>
                  </Typography>
                </Alert>
              )}

            {confluenceResult && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {confluenceResult.message || 'Export successful!'}
                </Typography>
                {confluenceResult.link && (
                  <Link
                    href={confluenceResult.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 1,
                      color: 'info.dark',
                      fontWeight: 'bold',
                    }}
                  >
                    Open Confluence Page
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                )}
              </Alert>
            )}

            {confluenceError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">{confluenceError}</Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {confluenceResult ? 'Close' : 'Cancel'}
        </Button>
        {!confluenceResult && (
          <Button
            onClick={handleExport}
            variant="contained"
            startIcon={
              confluenceExporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={
              (exportFormat === 'excel' && resultsToExport === 0) ||
              (exportFormat === 'confluence' && isConfluenceDisabled) ||
              confluenceExporting
            }
            sx={{
              bgcolor: exportFormat === 'confluence' ? 'info.dark' : 'primary.main',
              '&:hover': {
                bgcolor: exportFormat === 'confluence' ? 'info.dark' : 'primary.light',
              },
            }}
          >
            {exportFormat === 'excel'
              ? `Export ${resultsToExport} ${resultsToExport === 1 ? 'Result' : 'Results'}`
              : confluenceExporting
                ? 'Exporting...'
                : 'Export to Confluence'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
